import { Link } from 'react-router-dom'
import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  StatBand,
  StatusPill,
  UptimeBar,
  Timeline,
  Reveal,
} from './kit.jsx'

const COMPONENTS = [
  { name: 'Web app (CloudFront)', state: 'operational' },
  { name: 'Leads API (API Gateway + Lambda)', state: 'operational' },
  { name: 'Database (DynamoDB)', state: 'operational' },
  { name: 'Authentication (Cognito)', state: 'operational' },
  { name: 'Gmail integration', state: 'operational' },
  { name: 'Email delivery', state: 'operational' },
]

const UPTIME_STATS = [
  { value: '99.98%', label: '90-day uptime, all services' },
  { value: '99.99%', label: 'Leads API availability' },
  { value: '41m', label: 'Mean time to recovery' },
  { value: '0', label: 'Open incidents' },
]

const INCIDENTS = [
  {
    date: 'Jun 27, 2026',
    tag: 'Resolved',
    title: 'Elevated email delivery latency',
    body: 'A queue backlog at our sending provider delayed some sequence sends by up to 12 minutes. Deliverability was unaffected and every queued message was sent once throughput recovered.',
    bullets: [
      'Detected 09:14 PT, mitigated 09:41 PT, resolved 09:55 PT',
      'No Gmail-connected accounts were rate-limited or suspended',
      'Added surge headroom to the send worker pool',
    ],
  },
  {
    date: 'May 29, 2026',
    tag: 'Resolved',
    title: 'Degraded lead search in three regions',
    body: 'A hot partition on a regional DynamoDB table slowed filtered lead queries for the Southeast, Gulf, and Mid-Atlantic regions. Reads stayed correct but P95 latency rose above 2s for roughly 25 minutes.',
    bullets: [
      'Detected 14:02 PT, resolved 14:27 PT',
      'Repartitioned the affected tables and raised read capacity',
      'Other 47 regions were unaffected',
    ],
  },
  {
    date: 'Apr 23, 2026',
    tag: 'Resolved',
    title: 'Leads API outage (us-east-1)',
    body: 'A bad Lambda deploy returned 5xx responses on the Leads API for 38 minutes. The web app stayed online but lead tables and sequences could not load. We rolled back and hardened the deploy pipeline.',
    bullets: [
      'Detected 07:36 PT, rolled back and resolved 08:14 PT',
      'Automated canary checks now gate every API release',
      'No data was lost; outreach resumed automatically',
    ],
  },
]

export default function StatusPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="System status"
        description="Live operational status for Revailo Sales — the web app, Leads API, DynamoDB, Cognito auth, Gmail integration, and email delivery, with 90-day uptime and past incidents."
        path="/status"
        keywords="Revailo Sales status, system status, uptime, incident history, service health"
      />

      <PageHero
        eyebrow="Status"
        title="All systems operational"
        lead="Live health for every part of the Revailo Sales platform. This page updates automatically and reflects the last 90 days of uptime."
      >
        <Reveal>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ transform: 'scale(1.6)', transformOrigin: 'center' }}>
              <StatusPill state="operational" />
            </div>
            <span style={{ fontSize: 13, color: 'var(--mk-faint)', marginTop: 8 }}>
              Last checked Jul 18, 2026 · 08:00 PT
            </span>
          </div>
        </Reveal>
      </PageHero>

      <Section sm>
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Current status"
              title="Component health"
              lead="Each service is monitored independently with automated health checks every 60 seconds."
              align="left"
            />
          </Reveal>
          <div className="mt-10" style={{ maxWidth: 820 }}>
            <Reveal>
              {COMPONENTS.map((c) => (
                <div key={c.name} className="flex items-center justify-between gap-x-4 rounded-[14px] border border-[#e6e9ef] bg-white px-[22px] py-[18px] [&:not(:first-child)]:mt-[10px]">
                  <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--mk-ink)' }}>{c.name}</span>
                  <StatusPill state={c.state} />
                </div>
              ))}
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section sm>
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Uptime"
              title="Last 90 days"
              lead="Each bar is one day of platform availability. Hover a bar to see the date."
              align="left"
            />
          </Reveal>
          <div className="mt-10" style={{ maxWidth: 820 }}>
            <Reveal>
              <UptimeBar days={90} incidents={{ 5: 'outage', 40: 'degraded' }} />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: 12,
                  fontSize: 13,
                  color: 'var(--mk-faint)',
                }}
              >
                <span>90 days ago</span>
                <span>Today</span>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="py-14">
        <Container>
          <Reveal>
            <StatBand stats={UPTIME_STATS} />
          </Reveal>
        </Container>
      </Section>

      <Section>
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Incident history"
              title="Past incidents"
              lead="Every incident from the last 90 days, with what happened and what we changed to prevent it."
              align="left"
            />
          </Reveal>
          <div className="mt-10">
            <Reveal>
              <Timeline items={INCIDENTS} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section sm>
        <Container>
          <Reveal>
            <p style={{ fontSize: 15, color: 'var(--mk-ink-soft)', maxWidth: 640 }}>
              Seeing something we haven't caught yet? Our team monitors these services around the clock — reach out and we'll investigate right away.{' '}
              <Link to="/contact" style={{ color: 'var(--mk-accent)', fontWeight: 600 }}>
                Contact support
              </Link>
              .
            </p>
          </Reveal>
        </Container>
      </Section>
    </MarketingShell>
  )
}
